// @flow
import { useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';

import ApplicationService from '../../../services/applicationService';
import { invalidateHudReopenRollbackQueries } from '../../GeneralAffordable/hooks';
import { mapFloorPlanOptions } from '../../../utils/floorPlan';
import { NO_PREFERENCE_UUID } from '../../../utils/noPreference';
import { filterFloorPlanOptions } from '../../ConvertToApplicantModal/utils/bedrooms';
import type { FloorPlan } from '../../ManageFloorPlans/types';

/**
 * The plans this household can be qualified against: the property's affordable plans, narrowed to
 * the bedroom sizes the application asked for.
 *
 * `No Preference` is dropped because AC5 makes the field required. The stored plan is kept
 * regardless of the filters — the convert wizard applies neither of them, so it can store a plan
 * this list would otherwise exclude, and the select must not open blank on a real value. A plan
 * only reachable that way can be re-selected but never newly selected, and re-saving an unchanged
 * value writes nothing.
 */
export const useFloorPlanOptions = ({
  floorPlans,
  nBedsArr,
  storedFloorPlanId,
}: {
  floorPlans: ?(FloorPlan[]),
  nBedsArr?: ?Array<string | number>,
  storedFloorPlanId?: ?string,
}) =>
  useMemo(() => {
    const allOptions = mapFloorPlanOptions(floorPlans ?? []).filter(
      ({ value }) => value !== NO_PREFERENCE_UUID,
    );
    const withinBedrooms = new Set(
      filterFloorPlanOptions(allOptions, nBedsArr).map(({ value }) => value),
    );

    // Filtering the full list rather than concatenating the stored option keeps mapFloorPlanOptions'
    // bedroom ordering intact.
    return allOptions.filter(
      (option) =>
        option.value === storedFloorPlanId ||
        (withinBedrooms.has(option.value) &&
          (option.affordablePrograms?.length ?? 0) > 0),
    );
  }, [floorPlans, nBedsArr, storedFloorPlanId]);

export const useChangeQualifyingFloorPlan = ({
  organizationId,
  propertyId,
  applicationId,
  onSaved,
}: {
  organizationId: string,
  propertyId: string,
  applicationId: string,
  onSaved: Function,
}) => {
  const applicationService = useMemo(() => new ApplicationService(), []);
  const queryClient = useQueryClient();

  return useMutation(
    (floorPlanId) =>
      applicationService.changeQualifyingFloorPlan(
        organizationId,
        propertyId,
        applicationId,
        floorPlanId,
      ),
    {
      onSuccess: () => {
        toastr.success('Success', 'Floor plan updated successfully');
        // The qualification tabs read react-query; the Snapshot row reads redux, which is why the
        // container still refetches the application in onSaved.
        invalidateHudReopenRollbackQueries(queryClient);
        onSaved?.();
      },
      // requestAPI rejects a plain string for a server error but `{ networkError }` for a
      // connection failure (`utils/api.js`), and that object renders as [object Object].
      onError: (e) => toastr.error('Error', e?.message ?? e?.networkError ?? e),
    },
  );
};
